import { Dispatch, SetStateAction } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Board from '@/components/Board';
import CandidateBoardCard from '@/components/CandidateBoardCard';
import CandidateListCard from '@/components/CandidateListCard';
import CandidateSort from '@/components/CandidateSort';
import HeaderTabSelector from '@/components/HeaderTabSelector';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import { getEndpointWithParams } from '@/routes/config';
import { JobCandidatesByStage, JobCandidateStage } from '@/services/jobs';
import { PersonType } from '@/types/api';
import { Paths } from '@/types/routes';
import { DEFAULT_STATUS_COLOR, getFormattedStatus } from '@/utils/format';
import { onScrollToSmoothly } from '@/utils/global';
import * as Styles from './JobCandidates.styles';

type JobCandidatesComponentProps = {
  candidates?: JobCandidatesByStage;
  onGoToProfile: (profileId: string) => void;
  showRemoveModal?: boolean;
  onRemoveCandidate: (candidateId: string) => void;
  layout?: Layout;
  onChangeLayout?: (value: Layout) => void;
  candidateSearchInputText?: string;
  setCandidateSearchInputText?: (value: string) => void;
  onAddTalent: (talentId: string) => void;
  isAddTalentOpen: boolean;
  setIsAddTalentOpen: (value: boolean) => void;
  candidateStatuses: ReturnType<typeof useCandidateStatuses>;
  sortBy: { order: string; column: string };
  setSortBy: Dispatch<SetStateAction<{ order: string; column: string }>>;
  allCandidates?: PersonType[];
  candidateSearchLoading: boolean;
};

const CANDIDATE_CATEGORIES_LABEL: { [key in JobCandidateStage]: string } = {
  HIRED: 'Hired',
  OFFER_MADE: 'Offer Made',
  SUBMITTED_TO_PARTNER: 'Submitted to Partner',
  READY_TO_HIRE: 'Ready to Hire',
  NOT_READY: 'Not Ready',
  FAILED: 'Failed',
};

const JOB_CANDIDATES_STAGE: Array<JobCandidateStage> = [
  'HIRED',
  'OFFER_MADE',
  'SUBMITTED_TO_PARTNER',
  'READY_TO_HIRE',
  'NOT_READY',
  'FAILED',
];

export type Layout = 'list' | 'board';
const layouts: Layout[] = ['list', 'board'];

const layoutLabels: { [layout in Layout]: string } = {
  list: 'List',
  board: 'Board',
};

const TABLE_OF_CONTENTS = JOB_CANDIDATES_STAGE.map((category) => ({
  name: CANDIDATE_CATEGORIES_LABEL[category],
  id: category,
  scrollTo: () => onScrollToSmoothly(category),
}));

const JobCandidatesComponent = function ({
  candidates,
  onGoToProfile,
  onRemoveCandidate,
  layout = 'list',
  onChangeLayout,
  candidateSearchInputText,
  setCandidateSearchInputText,
  onAddTalent,
  isAddTalentOpen,
  setIsAddTalentOpen,
  candidateStatuses,
  sortBy,
  setSortBy,
  allCandidates = [],
  candidateSearchLoading,
}: JobCandidatesComponentProps): JSX.Element {
  const sortCandidates = (a: PersonType, b: PersonType): number => {
    let compare = 0;

    if (sortBy.column === 'skills') {
      compare = a.skills.length - b.skills.length;
    } else {
      compare = (a[sortBy.column] || '').localeCompare(b[sortBy.column] || '');
    }

    return compare * (sortBy.order === 'ASC' ? 1 : -1);
  };

  const renderListContent = (): JSX.Element => (
    <Styles.ListContent>
      <Styles.Sidebar tableOfContentOptions={TABLE_OF_CONTENTS} />
      <Styles.Content>
        {JOB_CANDIDATES_STAGE.map((category) => (
          <Styles.CandidateCategoryBox key={category} id={category}>
            <Styles.CandidateCategoryBoxTitleContainer>
              <strong>{CANDIDATE_CATEGORIES_LABEL[category]}</strong>
              <span>
                {candidates?.[category].length || 'No'}{' '}
                {candidates?.[category].length === 1 ? 'Candidate' : 'Candidates'}
              </span>
            </Styles.CandidateCategoryBoxTitleContainer>
            {(candidates?.[category] || []).length === 0 ? (
              <Styles.CandidateBoxEmptyState>
                There’s no candidates on this stage.
              </Styles.CandidateBoxEmptyState>
            ) : (
              <Styles.CandidateList>
                {(candidates?.[category] || []).map((candidate) => (
                  <CandidateListCard
                    border="solid"
                    displaySourceInfo
                    person={candidate}
                    key={`${category}-${candidate.id}`}
                    menuItems={[
                      {
                        label: 'Visit profile',
                        menuItemIcon: OpenInNewIcon,
                        callback: (e) => {
                          e.stopPropagation();
                          onGoToProfile(candidate.id);
                        },
                      },
                      {
                        label: 'Remove from job',
                        menuItemOptions: { warning: true },
                        menuItemIcon: DeleteIcon,
                        callback: (e) => {
                          e.stopPropagation();
                          onRemoveCandidate(candidate.id);
                        },
                      },
                    ]}
                  />
                ))}
              </Styles.CandidateList>
            )}
          </Styles.CandidateCategoryBox>
        ))}
      </Styles.Content>
    </Styles.ListContent>
  );

  const renderBoardContent = (): JSX.Element => (
    <Styles.BoardContent>
      <Board>
        {JOB_CANDIDATES_STAGE.map((status) => {
          const formattedStatus = getFormattedStatus(status);
          const compareLabelAndStatus = (s: any): boolean => {
            return getFormattedStatus(s?.label) === formattedStatus;
          };
          const filteredStage = candidateStatuses.find(compareLabelAndStatus);
          const stageColor = filteredStage?.color ?? DEFAULT_STATUS_COLOR;

          const filteredCandidates = candidates?.[status] || [];

          return (
            <Board.Column key={status}>
              <Board.ColumnTitle
                title={formattedStatus}
                titleColor={stageColor}
                count={filteredCandidates.length}
              />
              <Styles.BoardCards>
                {filteredCandidates.sort(sortCandidates).map((talent) => {
                  const candidateLink = getEndpointWithParams(Paths.CANDIDATE_DETAILS, {
                    id: talent.id,
                  });

                  return (
                    <CandidateBoardCard
                      key={`${talent.id}`}
                      candidateLink={candidateLink}
                      firstName={talent.firstName}
                      lastName={talent.lastName}
                      status={talent.status || undefined}
                      inStatusSince={new Date(talent.updatedAt).toISOString()}
                      owner={talent.candidateOwner || ''}
                      stack={talent.skills?.map((skill) => skill.skill.name)}
                      menuItems={[
                        {
                          label: 'Visit profile',
                          menuItemIcon: OpenInNewIcon,
                          callback: (e) => {
                            e.stopPropagation();
                            onGoToProfile(talent.id);
                          },
                        },
                        {
                          label: 'Remove from job',
                          menuItemOptions: { warning: true },
                          menuItemIcon: DeleteIcon,
                          callback: (e) => {
                            e.stopPropagation();
                            onRemoveCandidate(talent.id);
                          },
                        },
                      ]}
                    />
                  );
                })}
              </Styles.BoardCards>
            </Board.Column>
          );
        })}
      </Board>
    </Styles.BoardContent>
  );

  const onUpdateSortBy = (event: any): void => {
    const value = event.target.value as string;
    const name = event.target.name as 'order' | 'column';

    setSortBy((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div>
      <Styles.SubHeader>
        <Styles.Grow>
          <HeaderTabSelector
            options={layouts}
            value={layout}
            onChange={onChangeLayout}
            labels={layoutLabels}
          />
        </Styles.Grow>
        {layout === 'board' && (
          <CandidateSort
            label="Sort Columns by"
            {...sortBy}
            onUpdateSortBy={onUpdateSortBy}
            isBigger={false}
            outline
          />
        )}
        <Styles.CandidateSearch
          searchValue={candidateSearchInputText}
          onSearchChange={setCandidateSearchInputText}
          allTalents={allCandidates}
          onAddTalent={onAddTalent}
          showInput={isAddTalentOpen}
          onShowInputChange={setIsAddTalentOpen}
          loading={candidateSearchLoading}
        />
      </Styles.SubHeader>
      {layout === 'list' ? renderListContent() : renderBoardContent()}
    </div>
  );
};

export default JobCandidatesComponent;
